import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Ported as-is from the old AdminPanel.tsx "telegram" tab (Admin Panel
// Redesign didn't call for changes here — just its own section + independent
// data fetching instead of sharing loadData()/activeTab with everything else).
export default function TelegramSection() {
  const { toast } = useToast()
  const [telegramGroups, setTelegramGroups] = useState<any[]>([])
  const [telegramLoading, setTelegramLoading] = useState(false)
  const [telegramError, setTelegramError] = useState<string | null>(null)
  const [telegramGroupsInput, setTelegramGroupsInput] = useState('')

  const loadGroups = async () => {
    try {
      setTelegramLoading(true)
      setTelegramError(null)
      const res = await apiClient.getAdminTelegramGroups()
      setTelegramGroups((res as any).groups || [])
    } catch (e: any) {
      console.error('Failed to load Telegram groups', e)
      setTelegramError(e.message || 'Failed to load Telegram groups')
    } finally {
      setTelegramLoading(false)
    }
  }

  useEffect(() => {
    loadGroups()
  }, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telegram Monitoring</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Configure Telegram groups and channels to monitor via Telescope. Requires a valid TELESCOPE_API_KEY.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Add groups or channels (one per line)</Label>
          <textarea
            className="w-full min-h-[100px] text-sm rounded border border-input bg-transparent p-2 font-mono"
            placeholder={'@group1\nhttps://t.me/group2\nchannel_name3'}
            value={telegramGroupsInput}
            onChange={(e) => setTelegramGroupsInput(e.target.value)}
          />
          <Button
            disabled={telegramLoading}
            onClick={async () => {
              const lines = telegramGroupsInput
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0)
              if (!lines.length) {
                toast({ title: 'Enter at least one group or channel handle', variant: 'destructive' })
                return
              }
              try {
                setTelegramLoading(true)
                setTelegramError(null)
                const res = await apiClient.adminAddTelegramGroups(lines)
                const errors = (res as any).errors || []
                if (errors.length) {
                  toast({ title: `Added ${((res as any).added || []).length} groups, ${errors.length} failed`, variant: 'destructive' })
                  console.error('Telegram add errors', errors)
                } else {
                  toast({ title: `Added ${((res as any).added || []).length} groups` })
                }
                setTelegramGroupsInput('')
                await loadGroups()
              } catch (e: any) {
                console.error('Failed to add Telegram groups', e)
                setTelegramError(e.message || 'Failed to add Telegram groups')
              } finally {
                setTelegramLoading(false)
              }
            }}
          >
            {telegramLoading ? 'Saving…' : 'Add Groups'}
          </Button>
          {telegramError && <p className="text-xs text-destructive mt-1">{telegramError}</p>}
        </div>

        <div className="border rounded p-3 max-h-80 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Monitored Groups / Channels</h3>
            <span className="text-xs text-muted-foreground">
              {telegramLoading ? 'Loading…' : `${telegramGroups.length} total`}
            </span>
          </div>
          {telegramGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No groups configured yet. Add Telegram @handles or t.me URLs above.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-1 pr-2">Username</th>
                  <th className="py-1 pr-2">Title</th>
                  <th className="py-1 pr-2">ID</th>
                </tr>
              </thead>
              <tbody>
                {telegramGroups.map((g: any, idx: number) => (
                  <tr key={g.group_id || g.id || idx} className="border-b border-border/40">
                    <td className="py-1 pr-2 font-mono text-sky-400">
                      {g.username ? `@${g.username}` : g.name || '—'}
                    </td>
                    <td className="py-1 pr-2">{g.name || g.title || '—'}</td>
                    <td className="py-1 pr-2 font-mono text-muted-foreground">{g.group_id || g.id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
